import { Link, useNavigate } from 'react-router-dom';
import { useHome } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { Loader, ErrorView } from '../components/StateViews';
import { Avatar } from '../components/Avatar';
import { Watermark } from '../components/Watermark';
import { haptics, openTelegramLink } from '../telegram';
import { track } from '../analytics';
import { compact, money, shortDate, timeLabel } from '../format';
import type { NewsItem } from '../api/types';

const suit: Record<string, string> = {
  spade: '♠',
  diamond: '♦',
  heart: '♥',
  club: '♣',
};

function NewsRow({ n }: { n: NewsItem }) {
  const sym = suit[n.icon ?? ''] ?? '♦';
  const color = n.icon === 'spade' || n.icon === 'club' ? 'var(--green)' : 'var(--gold)';
  return (
    <div className="news-item">
      <div className="news-suit" style={{ color }}>
        {sym}
      </div>
      <div>
        <div className="ttl">{n.title}</div>
        {n.body && <div className="bd">{n.body}</div>}
      </div>
    </div>
  );
}

export function HomeScreen() {
  const { player } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useHome();

  if (isLoading) return <Loader />;
  if (isError || !data) return <ErrorView message={(error as Error)?.message} />;

  const t = data.nextTournament;
  const live = t?.status === 'in_progress';
  const meta = t
    ? [shortDate(t.startTime), timeLabel(t.startTime), t.format].filter(Boolean).join(' · ')
    : '';

  return (
    <div>
      <Watermark />
      <div className="home-header">
        <img className="logo" src="/logo.png" alt="Straddle" />
        <Link to="/profile">
          <Avatar name={player?.displayName} src={player?.photoUrl} size={42} bordered />
        </Link>
      </div>

      {t ? (
        // Вся карточка — одна кликабельная зона (нижняя кнопка-Link гасит
        // всплытие, чтобы «Смотреть Live» не давал двойную навигацию).
        <div
          data-theme="dark"
          className="featured"
          role="button"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/tournaments/${t.id}`)}
        >
          <div className="label">{live ? '● Сейчас идёт' : 'Ближайший турнир'}</div>
          <div className="name">{t.title ?? t.format ?? 'Турнир'}</div>
          <div className="meta">{meta}</div>
          <div className="cards">
            <div className="detail-card">
              <div className="v">{t.buyIn > 0 ? money(t.buyIn) : 'Free'}</div>
              <div className="l">Бай-ин</div>
            </div>
            {t.capacity && t.capacity > 0 ? (
              <div className="detail-card">
                <div className="v gold">
                  {t.registeredCount}/{t.capacity}
                </div>
                <div className="l">Свободно</div>
              </div>
            ) : t.guarantee ? (
              <div className="detail-card">
                <div className="v gold">{compact(t.guarantee)} ₽</div>
                <div className="l">Гарантия</div>
              </div>
            ) : (
              <div className="detail-card">
                <div className="v gold">{t.registeredCount}</div>
                <div className="l">Записались</div>
              </div>
            )}
          </div>
          {live ? (
            <>
              <Link
                to={`/tournaments/${t.id}/live`}
                className="btn"
                style={{ display: 'block', textAlign: 'center' }}
                onClick={(e) => {
                  e.stopPropagation();
                  track('live_click', { gameId: t.id, from: 'home' });
                }}
              >
                ● Смотреть Live
              </Link>
              {/* Идёт, но поздняя регистрация ещё открыта (registrationOpen
                  наложен сервером из live-данных Korin) — дать путь к записи. */}
              {t.registrationOpen && !t.registeredByMe && (
                <Link
                  to={`/tournaments/${t.id}`}
                  className="btn btn--ghost"
                  style={{ display: 'block', textAlign: 'center', marginTop: 8 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Записаться{t.buyIn > 0 ? ` · ${money(t.buyIn)}` : ''}
                </Link>
              )}
            </>
          ) : (
            <Link
              to={`/tournaments/${t.id}`}
              className="btn"
              style={{ display: 'block', textAlign: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              {t.registeredByMe
                ? 'Вы записаны'
                : `Записаться${t.buyIn > 0 ? ` · ${money(t.buyIn)}` : ''}`}
            </Link>
          )}
        </div>
      ) : (
        <div className="card muted" style={{ marginBottom: 22 }}>
          Ближайших турниров пока нет
        </div>
      )}

      <div className="section-header">
        <h2>Новости клуба</h2>
      </div>
      {data.news.length === 0 ? (
        <div className="muted tiny">Пока нет новостей</div>
      ) : (
        data.news.map((n) => <NewsRow key={n.id} n={n} />)
      )}

      <button
        className="btn btn--ghost"
        style={{ display: 'block', width: '100%', textAlign: 'center', margin: '22px 0 4px' }}
        onClick={() => {
          haptics.tap();
          track('help_click');
          openTelegramLink('https://telegram.me/straddle_assistant');
        }}
      >
        💬 Нужна помощь? Напишите нам
      </button>
    </div>
  );
}
